/**
 * Role 17 — Statement of Cash Flows (Direct) — calculated totals.
 *
 * Extracted verbatim from the former single-class `CalculationService`, which held
 * all 13 sheets' total calculations in one 5,557-line file. Each function is pure and
 * self-contained (no shared state, no `this`), so the split is a move, not a rewrite.
 * `CalculationService` remains the injectable entry point and delegates here.
 */

/**
 * Calculate all Role17 (510000 - Statement of Cash Flows, Direct Method) totals.
 *
 * Six derived rows: Cash Generated from Operations, Net Cash from Operating,
 * Net Cash Used in Investing, Net Cash from Financing, Net Increase/Decrease
 * in Cash, Cash at End of Year. The investing-section sign convention follows
 * the existing component logic (`-purchasePPE + proceedsPPE - purchaseIntangibles - …`).
 *
 * @param formData - The form data object (control name → value)
 * @param suffixes - Resolved column suffixes
 * @returns Object containing only the calculated total fields
 */
export function calculateTotals_510000(
  formData: Record<string, any>,
  suffixes: ReadonlyArray<string>,
): Record<string, any> {
  const calculatedTotals: Record<string, any> = {};

  const num = (key: string): number => {
    const raw = formData[key];
    if (raw === null || raw === undefined || raw === '') return 0;
    const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  for (const suffix of suffixes) {
    // 1. Cash generated from operations
    const cashReceipts = num(`ssmt:CashReceiptsFromCustomers${suffix}`);
    const cashPaid = num(`ssmt:CashPaidToSuppliersAndEmployees${suffix}`);
    const cashGenerated = cashReceipts - cashPaid;
    calculatedTotals[`ssmt:CashGeneratedFromOperations${suffix}`] =
      cashGenerated;

    // 2. Net cash from operating activities
    const interestPaid = num(`ssmt:InterestPaid${suffix}`);
    const taxesPaid = num(`ssmt:IncomeTaxesPaid${suffix}`);
    const interestReceived = num(`ssmt:InterestReceived${suffix}`);
    const dividendsReceived = num(`ssmt:DividendsReceived${suffix}`);
    const netCashFromOperating =
      cashGenerated -
      interestPaid -
      taxesPaid +
      interestReceived +
      dividendsReceived;
    calculatedTotals[`ssmt:NetCashFromOperatingActivities${suffix}`] =
      netCashFromOperating;

    // 3. Net cash used in investing activities
    const purchasePPE = num(
      `ssmt:PurchaseOfPropertyPlantAndEquipment${suffix}`,
    );
    const proceedsPPE = num(
      `ssmt:ProceedsFromDisposalOfPropertyPlantAndEquipment${suffix}`,
    );
    const purchaseIntangibles = num(
      `ssmt:PurchaseOfIntangibleAssets${suffix}`,
    );
    const purchaseInvestments = num(`ssmt:PurchaseOfInvestments${suffix}`);
    const proceedsInvestments = num(
      `ssmt:ProceedsFromDisposalOfInvestments${suffix}`,
    );
    const acquisitionSubs = num(`ssmt:AcquisitionOfSubsidiaries${suffix}`);
    const netCashInvesting =
      -purchasePPE +
      proceedsPPE -
      purchaseIntangibles -
      purchaseInvestments +
      proceedsInvestments -
      acquisitionSubs;
    calculatedTotals[`ssmt:NetCashUsedInInvestingActivities${suffix}`] =
      netCashInvesting;

    // 4. Net cash from financing activities
    const proceedsShareCapital = num(
      `ssmt:ProceedsFromIssuanceOfShareCapital${suffix}`,
    );
    const proceedsBorrowings = num(`ssmt:ProceedsFromBorrowings${suffix}`);
    const repaymentBorrowings = num(`ssmt:RepaymentOfBorrowings${suffix}`);
    const paymentLeases = num(`ssmt:PaymentOfLeaseLiabilities${suffix}`);
    const dividendsPaid = num(`ssmt:DividendsPaid${suffix}`);
    const dividendsNCI = num(
      `ssmt:DividendsPaidToNonControllingInterests${suffix}`,
    );
    const netCashFinancing =
      proceedsShareCapital +
      proceedsBorrowings -
      repaymentBorrowings -
      paymentLeases -
      dividendsPaid -
      dividendsNCI;
    calculatedTotals[`ssmt:NetCashFromFinancingActivities${suffix}`] =
      netCashFinancing;

    // 5. Net increase/(decrease) in cash
    const netIncrease =
      netCashFromOperating + netCashInvesting + netCashFinancing;
    calculatedTotals[
      `ssmt:NetIncreaseDecreaseInCashAndCashEquivalents${suffix}`
    ] = netIncrease;

    // 6. Cash at end of year
    const cashBeginning = num(
      `ssmt:CashAndCashEquivalentsAtBeginningOfYear${suffix}`,
    );
    const fxEffects = num(`ssmt:EffectOfExchangeRateChanges${suffix}`);
    calculatedTotals[`ssmt:CashAndCashEquivalentsAtEndOfYear${suffix}`] =
      cashBeginning + netIncrease + fxEffects;
  }

  return calculatedTotals;
}
